import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Document>} */
  #cachedHtml = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    console.log('[QuickAdd] Component connected');
    
    // Add direct click listener as fallback
    const button = this.querySelector('.quick-add__button--choose');
    if (button) {
      button.addEventListener('click', this.handleClick);
      console.log('[QuickAdd] Direct click listener added to button');
    }
    
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }
  
  disconnectedCallback() {
    super.disconnectedCallback();

    const button = this.querySelector('.quick-add__button--choose');
    if (button) {
      button.removeEventListener('click', this.handleClick);
    }

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    console.log('[QuickAdd] handleClick called');
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;
    console.log('[QuickAdd] Product URL:', currentUrl);
    if (!currentUrl) {
      console.error('[QuickAdd] No product URL found');
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    console.log('[QuickAdd] Cached content found:', !!productGrid);

    if (!productGrid) {
      console.log('[QuickAdd] Fetching product page...');
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      console.log('[QuickAdd] Product page fetched:', !!html);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        console.log('[QuickAdd] Grid element found:', !!gridElement);
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
          console.log('[QuickAdd] Content cached');
        } else {
          console.error('[QuickAdd] Could not find [data-product-grid-content] in product page');
          // Try to find alternative selectors
          const alternative = html.querySelector('.product-information__grid, .product-information');
          console.log('[QuickAdd] Alternative selector found:', !!alternative);
        }
      } else {
        console.error('[QuickAdd] Failed to fetch product page');
      }
    }

    if (productGrid) {
      console.log('[QuickAdd] Updating modal with content...');
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      
      // First, open the dialog (but keep it hidden) so the content can be measured
      const dialogElement = /** @type {QuickAddDialog} */ (document.getElementById('quick-add-dialog'));
      if (dialogElement && dialogElement.refs && dialogElement.refs.dialog) {
        // Temporarily show the dialog to allow content to be measured
        dialogElement.refs.dialog.setAttribute('open', '');
        dialogElement.refs.dialog.style.display = 'flex';
        dialogElement.refs.dialog.style.visibility = 'hidden';
        dialogElement.refs.dialog.style.position = 'fixed';
        dialogElement.refs.dialog.style.top = '-9999px';
      }
      
      await this.updateQuickAddModal(freshContent);
      console.log('[QuickAdd] Modal updated');
      
      // Wait for content to be measured
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      
      // Check if content has size
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        const rect = modalContent.getBoundingClientRect();
        console.log('[QuickAdd] Modal content size after update:', { width: rect.width, height: rect.height });
      }
      
      // Reset dialog visibility
      if (dialogElement && dialogElement.refs && dialogElement.refs.dialog) {
        dialogElement.refs.dialog.removeAttribute('open');
        dialogElement.refs.dialog.style.display = '';
        dialogElement.refs.dialog.style.visibility = '';
        dialogElement.refs.dialog.style.position = '';
        dialogElement.refs.dialog.style.top = '';
      }
    } else {
      console.error('[QuickAdd] No product grid content available');
      return;
    }

    console.log('[QuickAdd] Opening modal...');
    this.#openQuickAddModal();
    console.log('[QuickAdd] Modal open called');
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    console.log('[QuickAdd] Looking for dialog element:', dialogComponent);
    if (!dialogComponent) {
      console.error('[QuickAdd] Could not find quick-add-dialog element');
      return;
    }
    console.log('[QuickAdd] Dialog element found, checking instance...');
    if (!(dialogComponent instanceof QuickAddDialog)) {
      console.error('[QuickAdd] quick-add-dialog is not an instance of QuickAddDialog', dialogComponent);
      console.log('[QuickAdd] Dialog element type:', dialogComponent.constructor.name);
      return;
    }
    console.log('[QuickAdd] Dialog is valid instance, staying visible...');
    this.#stayVisibleUntilDialogCloses(dialogComponent);

    console.log('[QuickAdd] Calling showDialog...');
    dialogComponent.showDialog();
    console.log('[QuickAdd] showDialog called');
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) {
      console.error('[QuickAdd] fetchProductPage: No URL provided');
      return null;
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      console.log('[QuickAdd] Fetching:', productPageUrl);
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      console.log('[QuickAdd] Response status:', response.status, response.ok);
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      console.log('[QuickAdd] Response text length:', responseText.length);
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      console.log('[QuickAdd] HTML parsed');
      
      // Cache the full HTML document for image lookup
      this.#cachedHtml.set(productPageUrl, html);

      return html;
    } catch (error) {
      console.error('[QuickAdd] Fetch error:', error);
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    console.log('[QuickAdd] updateQuickAddModal called');
    console.log('[QuickAdd] productGrid:', productGrid);
    console.log('[QuickAdd] modalContent:', modalContent);

    if (!productGrid || !modalContent) {
      console.error('[QuickAdd] Missing productGrid or modalContent', { productGrid: !!productGrid, modalContent: !!modalContent });
      return;
    }

    console.log('[QuickAdd] productGrid children count:', productGrid.children.length);
    console.log('[QuickAdd] productGrid innerHTML length:', productGrid.innerHTML.length);
    
    // Log what elements are in the productGrid
    const mediaElement = productGrid.querySelector('.product-information__media');
    const detailsElement = productGrid.querySelector('.product-details');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productForm = productGrid.querySelector('product-form-component');
    const productPrice = productGrid.querySelector('product-price');
    const productTitle = productGrid.querySelector('.view-product-title, h1, .product-title');
    
    console.log('[QuickAdd] Media element:', !!mediaElement);
    console.log('[QuickAdd] Details element:', !!detailsElement);
    console.log('[QuickAdd] Variant picker:', !!variantPicker);
    console.log('[QuickAdd] Product form:', !!productForm);
    console.log('[QuickAdd] Product price:', !!productPrice);
    console.log('[QuickAdd] Product title:', !!productTitle);

    // Remove the media gallery from the product grid before inserting into modal
    if (mediaElement) {
      mediaElement.remove();
      console.log('[QuickAdd] Media gallery removed from modal content');
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      
      // Create a wrapper for thumbnail + title/price on mobile
      const isMobile = window.innerWidth <= 750;
      
      const headerContent = document.createElement('div');
      headerContent.classList.add('product-header-content');
      headerContent.style.display = 'grid';
      headerContent.style.gridTemplateColumns = isMobile ? '1fr' : '600px 1fr';
      headerContent.style.gap = 'var(--padding-md)';
      headerContent.style.alignItems = 'start';
      
      // Create thumbnail container
      const thumbnailContainer = document.createElement('div');
      thumbnailContainer.className = 'quick-add-thumbnail-container';
      thumbnailContainer.style.display = 'flex';
      thumbnailContainer.style.visibility = 'visible';
      thumbnailContainer.style.opacity = '1';
      
      if (isMobile) {
        thumbnailContainer.style.width = '100%';
        thumbnailContainer.style.maxWidth = '100%';
        thumbnailContainer.style.height = 'auto';
        thumbnailContainer.style.maxHeight = '300px';
      } else {
        thumbnailContainer.style.width = '600px';
        thumbnailContainer.style.height = '600px';
      }
      
      const thumbnailImg = document.createElement('img');
      thumbnailImg.className = 'quick-add-thumbnail';
      thumbnailImg.alt = '';
      thumbnailImg.loading = 'eager';
      thumbnailImg.style.display = 'none';
      
      if (isMobile) {
        thumbnailImg.style.width = '100%';
        thumbnailImg.style.maxWidth = '100%';
        thumbnailImg.style.height = 'auto';
        thumbnailImg.style.maxHeight = '300px';
        thumbnailImg.style.objectFit = 'contain';
      } else {
        thumbnailImg.style.width = '600px';
        thumbnailImg.style.height = '600px';
        thumbnailImg.style.objectFit = 'cover';
      }
      thumbnailContainer.appendChild(thumbnailImg);
      
      // Create title/price wrapper
      const titlePriceWrapper = document.createElement('div');
      titlePriceWrapper.classList.add('product-header-text');
      titlePriceWrapper.style.display = 'flex';
      titlePriceWrapper.style.flexDirection = 'column';
      titlePriceWrapper.style.gap = 'var(--padding-xs)';
      
      titlePriceWrapper.appendChild(productTitle);
      if (productPrice) {
        titlePriceWrapper.appendChild(productPrice);
      }
      
      headerContent.appendChild(thumbnailContainer);
      headerContent.appendChild(titlePriceWrapper);
      productHeader.appendChild(headerContent);
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    console.log('[QuickAdd] Before morph - modalContent children:', modalContent.children.length);
    morph(modalContent, productGrid);
    console.log('[QuickAdd] After morph - modalContent children:', modalContent.children.length);
    console.log('[QuickAdd] After morph - modalContent innerHTML length:', modalContent.innerHTML.length);
    
    // Wait for morph to complete
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    console.log('[QuickAdd] After morph wait - modalContent children:', modalContent.children.length);
    
    // Force single column layout
    const gridAfterMorph = modalContent.querySelector('.product-information__grid');
    const mediaAfterMorph = modalContent.querySelector('.product-information__media');
    const detailsAfterMorph = modalContent.querySelector('.product-details');
    
    if (gridAfterMorph) {
      const gridEl = /** @type {HTMLElement} */ (gridAfterMorph);
      gridEl.style.setProperty('display', 'flex', 'important');
      gridEl.style.setProperty('flex-direction', 'column', 'important');
      gridEl.style.setProperty('width', '100%', 'important');
      gridEl.style.setProperty('max-width', '100%', 'important');
    }
    
    if (mediaAfterMorph) {
      const mediaEl = /** @type {HTMLElement} */ (mediaAfterMorph);
      mediaEl.style.setProperty('order', '1', 'important');
      mediaEl.style.setProperty('width', '100%', 'important');
      mediaEl.style.setProperty('max-width', '100%', 'important');
    }
    
    if (detailsAfterMorph) {
      const detailsEl = /** @type {HTMLElement} */ (detailsAfterMorph);
      detailsEl.style.setProperty('order', '2', 'important');
      detailsEl.style.setProperty('display', 'flex', 'important');
      detailsEl.style.setProperty('flex-direction', 'column', 'important');
      detailsEl.style.setProperty('width', '100%', 'important');
      detailsEl.style.setProperty('max-width', '100%', 'important');
    }

    this.#syncVariantSelection(modalContent);
    
    // Debug: Check if title exists before adding thumbnail
    const titleBefore = modalContent.querySelector('.view-product-title');
    console.log('[QuickAdd] Title before thumbnail added:', !!titleBefore);
    if (titleBefore) {
      console.log('[QuickAdd] Title element before:', titleBefore);
      console.log('[QuickAdd] Title computed display before:', window.getComputedStyle(titleBefore).display);
    }
    
    this.#addVariantThumbnail(modalContent);
    
    // Update thumbnail after a delay to ensure all content is initialized
    setTimeout(() => {
      const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
      if (thumbnailContainer) {
        console.log('[QuickAdd] Updating thumbnail after delay...');
        this.#updateThumbnailImage(modalContent, thumbnailContainer);
      }
    }, 300);
    
    // Force title visibility after thumbnail is added
    const titleAfter = modalContent.querySelector('.view-product-title');
    if (titleAfter) {
      const titleEl = /** @type {HTMLElement} */ (titleAfter);
      titleEl.style.display = 'flex';
      titleEl.style.visibility = 'visible';
      titleEl.style.opacity = '1';
      titleEl.style.width = 'auto';
      titleEl.style.height = 'auto';
      titleEl.style.minWidth = '0';
      titleEl.style.maxWidth = 'none';
      titleEl.style.position = 'relative';
      titleEl.style.zIndex = '1';
      
      const titleLink = titleAfter.querySelector('a');
      if (titleLink) {
        const linkEl = /** @type {HTMLElement} */ (titleLink);
        // Set styles for proper display
        linkEl.style.display = '-webkit-box';
        linkEl.style.webkitBoxOrient = 'vertical';
        linkEl.style.webkitLineClamp = '2';
        linkEl.style.visibility = 'visible';
        linkEl.style.opacity = '1';
        linkEl.style.width = '100%';
        linkEl.style.minWidth = '0';
        linkEl.style.maxWidth = '100%';
        linkEl.style.height = 'auto';
        linkEl.style.overflow = 'hidden';
        linkEl.style.textOverflow = 'ellipsis';
        linkEl.style.color = 'inherit';
        linkEl.style.fontSize = 'var(--font-size--2xl)';
        linkEl.style.fontWeight = '600';
        linkEl.style.lineHeight = '1.2';
        linkEl.style.textDecoration = 'none';
      }
      
      // Check computed styles after forcing
      console.log('[QuickAdd] Title visibility forced');
      console.log('[QuickAdd] Title computed display after:', window.getComputedStyle(titleEl).display);
      console.log('[QuickAdd] Title computed visibility after:', window.getComputedStyle(titleEl).visibility);
      console.log('[QuickAdd] Title computed opacity after:', window.getComputedStyle(titleEl).opacity);
      console.log('[QuickAdd] Title computed width after:', window.getComputedStyle(titleEl).width);
      console.log('[QuickAdd] Title computed height after:', window.getComputedStyle(titleEl).height);
      console.log('[QuickAdd] Title getBoundingClientRect:', titleEl.getBoundingClientRect());
      
      if (titleLink) {
        const linkEl = /** @type {HTMLElement} */ (titleLink);
        console.log('[QuickAdd] Title link computed display after:', window.getComputedStyle(linkEl).display);
        console.log('[QuickAdd] Title link getBoundingClientRect:', linkEl.getBoundingClientRect());
      }
    } else {
      console.error('[QuickAdd] Title not found in modal content!');
    }
    
    // Listen for variant changes to update thumbnail
    const modalVariantPicker = modalContent.querySelector('variant-picker');
    if (modalVariantPicker) {
      // Listen to change events
      modalVariantPicker.addEventListener('change', () => {
        const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
        if (thumbnailContainer) {
          // Small delay to ensure variant is updated
          setTimeout(() => {
            this.#updateThumbnailImage(modalContent, thumbnailContainer);
          }, 100);
        }
      });
      
      // Listen to clicks on radio inputs (to catch clicks on disabled/unavailable variants)
      const allRadioInputs = modalVariantPicker.querySelectorAll('input[type="radio"]');
      allRadioInputs.forEach((input) => {
        input.addEventListener('click', (event) => {
          // Even if disabled, try to update thumbnail using the clicked input's variant ID
          const clickedInput = /** @type {HTMLInputElement} */ (event.target);
          const variantId = clickedInput.dataset.variantId;
          
          if (variantId) {
            // Temporarily store the clicked variant ID for the thumbnail update
            clickedInput.setAttribute('data-temp-selected', 'true');
            
            const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
            if (thumbnailContainer) {
              // Update thumbnail immediately using the clicked variant ID
              setTimeout(() => {
                this.#updateThumbnailImageForVariant(modalContent, thumbnailContainer, variantId);
                clickedInput.removeAttribute('data-temp-selected');
              }, 50);
            }
          } else {
            // Fallback to normal update
            const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
            if (thumbnailContainer) {
              setTimeout(() => {
                this.#updateThumbnailImage(modalContent, thumbnailContainer);
              }, 100);
            }
          }
        });
      });
      
      // Also listen to variant-selected event
      modalVariantPicker.addEventListener('variant-selected', () => {
        const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
        if (thumbnailContainer) {
          // Small delay to ensure variant is updated
          setTimeout(() => {
            this.#updateThumbnailImage(modalContent, thumbnailContainer);
          }, 100);
        }
      });
      
      // Also listen to variant-update event from ThemeEvents
      modalVariantPicker.addEventListener(ThemeEvents.variantUpdate, () => {
        const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
        if (thumbnailContainer) {
          // Small delay to ensure variant is updated
          setTimeout(() => {
            this.#updateThumbnailImage(modalContent, thumbnailContainer);
          }, 100);
        }
      });
    }
  }

  /**
   * Adds a thumbnail image next to the title showing the selected variant image
   * @param {Element} modalContent - The modal content element
   */
  #addVariantThumbnail(modalContent) {
    // First, check if thumbnail already exists (for mobile, it might be in product-header)
    let thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
    
    if (!thumbnailContainer) {
      // Try to find product-details first, if not found, try to find group-block-content directly
      let productDetails = modalContent.querySelector('.product-details');
      let groupBlockContent = null;
      
      if (productDetails) {
        groupBlockContent = productDetails.querySelector('.group-block-content');
      } else {
        // On mobile, product-details might not exist, try to find group-block-content directly
        groupBlockContent = modalContent.querySelector('.group-block-content');
        if (!groupBlockContent) {
          // Try to find it in product-information__grid
          const productGrid = modalContent.querySelector('.product-information__grid, [data-product-grid-content]');
          if (productGrid) {
            groupBlockContent = productGrid.querySelector('.group-block-content');
            productDetails = productGrid.querySelector('.product-details') || productGrid;
          }
        }
      }
      
      if (!groupBlockContent) {
        // On mobile, try to find product-header-content
        const headerContent = modalContent.querySelector('.product-header-content');
        if (headerContent) {
          // Thumbnail should already exist in headerContent from mobile setup
          thumbnailContainer = headerContent.querySelector('.quick-add-thumbnail-container');
          if (thumbnailContainer) {
            console.log('[QuickAdd] Thumbnail container found in product-header-content');
          } else {
            // Create thumbnail in headerContent
            thumbnailContainer = document.createElement('div');
            thumbnailContainer.className = 'quick-add-thumbnail-container';
            const containerEl = /** @type {HTMLElement} */ (thumbnailContainer);
            containerEl.style.display = 'flex';
            containerEl.style.visibility = 'visible';
            containerEl.style.opacity = '1';
            containerEl.style.width = '100px';
            containerEl.style.height = '100px';
            
            const thumbnailImg = document.createElement('img');
            thumbnailImg.className = 'quick-add-thumbnail';
            thumbnailImg.alt = '';
            thumbnailImg.loading = 'eager';
            const imgEl = /** @type {HTMLImageElement} */ (thumbnailImg);
            imgEl.style.display = 'none';
            imgEl.style.width = '100px';
            imgEl.style.height = '100px';
            imgEl.style.objectFit = 'cover';
            thumbnailContainer.appendChild(thumbnailImg);
            
            headerContent.insertBefore(thumbnailContainer, headerContent.firstChild);
            console.log('[QuickAdd] Thumbnail container created in product-header-content');
          }
        } else {
          console.log('[QuickAdd] Group block content and product-header-content not found');
          return;
        }
      } else {
        // Desktop: create thumbnail in group-block-content
        if (!productDetails) {
          productDetails = groupBlockContent.closest('.product-details') || groupBlockContent.parentElement;
        }

        thumbnailContainer = productDetails ? productDetails.querySelector('.quick-add-thumbnail-container') : null;
        if (!thumbnailContainer) {
          thumbnailContainer = document.createElement('div');
          thumbnailContainer.className = 'quick-add-thumbnail-container';
          const containerEl = /** @type {HTMLElement} */ (thumbnailContainer);
          containerEl.style.display = 'flex';
          containerEl.style.visibility = 'visible';
          containerEl.style.opacity = '1';
          
          const thumbnailImg = document.createElement('img');
          thumbnailImg.className = 'quick-add-thumbnail';
          thumbnailImg.alt = '';
          thumbnailImg.loading = 'eager';
          const imgEl = /** @type {HTMLImageElement} */ (thumbnailImg);
          imgEl.style.display = 'none';
          imgEl.style.visibility = 'hidden';
          imgEl.style.opacity = '0';
          thumbnailContainer.appendChild(thumbnailImg);
          
          // Insert at the beginning of group-block-content
          groupBlockContent.insertBefore(thumbnailContainer, groupBlockContent.firstChild);
          console.log('[QuickAdd] Thumbnail container created and inserted in group-block-content');
        }
      }
    } else {
      console.log('[QuickAdd] Thumbnail container already exists');
    }

    // Get current variant image
    this.#updateThumbnailImage(modalContent, thumbnailContainer);
    
    // Force thumbnail visibility on mobile with inline styles
    const containerEl = /** @type {HTMLElement} */ (thumbnailContainer);
    const isMobile = window.innerWidth <= 750;
    
    containerEl.style.setProperty('display', 'flex', 'important');
    containerEl.style.setProperty('visibility', 'visible', 'important');
    containerEl.style.setProperty('opacity', '1', 'important');
    
    if (isMobile) {
      // Mobile: full width, auto height, max 300px
      containerEl.style.setProperty('width', '100%', 'important');
      containerEl.style.setProperty('max-width', '100%', 'important');
      containerEl.style.setProperty('height', 'auto', 'important');
      containerEl.style.setProperty('min-width', '0', 'important');
      containerEl.style.setProperty('min-height', '0', 'important');
      containerEl.style.setProperty('max-height', '300px', 'important');
    } else {
      // Desktop: 600px fixed
      containerEl.style.setProperty('width', '600px', 'important');
      containerEl.style.setProperty('height', '600px', 'important');
      containerEl.style.setProperty('min-width', '600px', 'important');
      containerEl.style.setProperty('min-height', '600px', 'important');
      containerEl.style.setProperty('max-width', '600px', 'important');
      containerEl.style.setProperty('max-height', '600px', 'important');
    }
    
    const thumbnailImg = thumbnailContainer.querySelector('.quick-add-thumbnail');
    if (thumbnailImg) {
      const imgEl = /** @type {HTMLImageElement} */ (thumbnailImg);
      imgEl.style.setProperty('display', 'block', 'important');
      imgEl.style.setProperty('visibility', 'visible', 'important');
      imgEl.style.setProperty('opacity', '1', 'important');
      
      if (isMobile) {
        // Mobile: full width, auto height, max 300px, contain
        imgEl.style.setProperty('width', '100%', 'important');
        imgEl.style.setProperty('max-width', '100%', 'important');
        imgEl.style.setProperty('height', 'auto', 'important');
        imgEl.style.setProperty('min-width', '0', 'important');
        imgEl.style.setProperty('min-height', '0', 'important');
        imgEl.style.setProperty('max-height', '300px', 'important');
        imgEl.style.setProperty('object-fit', 'contain', 'important');
      } else {
        // Desktop: 600px fixed, cover
        imgEl.style.setProperty('width', '600px', 'important');
        imgEl.style.setProperty('height', '600px', 'important');
        imgEl.style.setProperty('min-width', '600px', 'important');
        imgEl.style.setProperty('min-height', '600px', 'important');
        imgEl.style.setProperty('max-width', '600px', 'important');
        imgEl.style.setProperty('max-height', '600px', 'important');
        imgEl.style.setProperty('object-fit', 'cover', 'important');
      }
    }
  }

  /**
   * Updates the thumbnail image for a specific variant ID
   * @param {Element} modalContent - The modal content element
   * @param {Element} thumbnailContainer - The thumbnail container element
   * @param {string} variantId - The variant ID to get the image for
   */
  #updateThumbnailImageForVariant(modalContent, thumbnailContainer, variantId) {
    const thumbnailImg = /** @type {HTMLImageElement | null} */ (thumbnailContainer.querySelector('.quick-add-thumbnail'));
    if (!thumbnailImg) return;

    console.log('[QuickAdd] Updating thumbnail image for variant ID:', variantId);

    // Get product ID
    const productForm = modalContent.querySelector('product-form-component');
    const productId = productForm instanceof HTMLElement ? productForm.dataset.productId : undefined;
    
    if (!productId) {
      console.log('[QuickAdd] No product ID found');
      thumbnailImg.style.display = 'none';
      return;
    }

    // Try to get variant image from product data JSON
    let productDataScript = modalContent.querySelector('script[type="application/json"][data-product-json]');
    if (!productDataScript) {
      productDataScript = modalContent.querySelector('script[type="application/json"]');
    }
    if (!productDataScript) {
      productDataScript = document.querySelector(`script[type="application/json"][data-product-id="${productId}"]`);
    }
    
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent || '{}');
        if (productData.variants && Array.isArray(productData.variants)) {
          const selectedVariant = productData.variants.find((/** @type {any} */ v) => v.id.toString() === variantId.toString());
          if (selectedVariant && selectedVariant.featured_media && selectedVariant.featured_media.preview_image) {
            let variantImgSrc = selectedVariant.featured_media.preview_image.src;
            if (variantImgSrc) {
              if (!variantImgSrc.startsWith('http')) {
                variantImgSrc = new URL(variantImgSrc, window.location.origin).toString();
              }
              
              try {
                const url = new URL(variantImgSrc);
                url.searchParams.set('width', '600');
                const finalSrc = url.toString();
                
                thumbnailImg.src = finalSrc;
                thumbnailImg.style.display = 'block';
                thumbnailImg.style.visibility = 'visible';
                thumbnailImg.style.opacity = '1';
                thumbnailImg.style.width = '100px';
                thumbnailImg.style.height = '100px';
                thumbnailImg.style.objectFit = 'cover';
                
                console.log('[QuickAdd] Using variant featured image (direct variant ID):', finalSrc);
                return;
              } catch (e) {
                console.error('[QuickAdd] Error creating variant image URL:', e);
              }
            }
          }
        }
      } catch (e) {
        console.error('[QuickAdd] Error parsing product data:', e);
      }
    }

    // Fallback: try to find image in cached HTML by variant ID
    const currentUrl = this.productPageUrl || '';
    const productPageUrl = currentUrl ? currentUrl.split('?')[0] : '';
    const cachedHtml = productPageUrl ? (this.#cachedHtml.get(productPageUrl) || this.#cachedHtml.get(this.productPageUrl)) : null;
    
    if (cachedHtml) {
      const variantImages = cachedHtml.querySelectorAll(`img[data-variant-id="${variantId}"], slideshow-slide[data-variant-id="${variantId}"] img`);
      if (variantImages.length > 0 && variantImages[0]) {
        const firstVariantImg = variantImages[0];
        let imgSrc = firstVariantImg.getAttribute('src') || firstVariantImg.getAttribute('data-src') || firstVariantImg.getAttribute('data-original-src') || '';
        if (imgSrc) {
          if (!imgSrc.startsWith('http')) {
            imgSrc = new URL(imgSrc, window.location.origin).toString();
          }
          try {
            const url = new URL(imgSrc);
            url.searchParams.set('width', '600');
            const finalSrc = url.toString();
            
            thumbnailImg.src = finalSrc;
            thumbnailImg.style.display = 'block';
            thumbnailImg.style.visibility = 'visible';
            thumbnailImg.style.opacity = '1';
            thumbnailImg.style.width = '100px';
            thumbnailImg.style.height = '100px';
            thumbnailImg.style.objectFit = 'cover';
            
            console.log('[QuickAdd] Using variant image from cached HTML (direct variant ID):', finalSrc);
            return;
          } catch (e) {
            console.error('[QuickAdd] Error creating variant image URL from cached HTML:', e);
          }
        }
      }
    }

    // If no image found, try the normal update method
    this.#updateThumbnailImage(modalContent, thumbnailContainer);
  }

  /**
   * Updates the thumbnail image based on selected variant color from product media alt text
   * @param {Element} modalContent - The modal content element
   * @param {Element} thumbnailContainer - The thumbnail container element
   */
  #updateThumbnailImage(modalContent, thumbnailContainer) {
    const thumbnailImg = /** @type {HTMLImageElement | null} */ (thumbnailContainer.querySelector('.quick-add-thumbnail'));
    if (!thumbnailImg) return;

    console.log('[QuickAdd] Updating thumbnail image based on color from alt text...');

    // Get selected color from variant picker
    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) {
      console.log('[QuickAdd] No variant picker found');
      thumbnailImg.style.display = 'none';
      return;
    }

    // Find color option (usually the first option or one containing 'color')
    const colorFieldset = variantPicker.querySelector('fieldset[data-option-name*="olor" i], fieldset[data-option-name*="or" i]');
    if (!colorFieldset) {
      // Try to find any fieldset if color fieldset not found
      const firstFieldset = variantPicker.querySelector('fieldset');
      if (!firstFieldset) {
        console.log('[QuickAdd] No color fieldset found');
        thumbnailImg.style.display = 'none';
        return;
      }
    }

    const selectedColorFieldset = colorFieldset || variantPicker.querySelector('fieldset');
    // Get the selected option, including disabled ones (unavailable variants)
    let selectedColorOption = /** @type {HTMLInputElement | null} */ (selectedColorFieldset?.querySelector('input[type="radio"]:checked, input[type="radio"][checked]'));
    
    // If no checked option found, try to find the one that was clicked (even if disabled)
    if (!selectedColorOption) {
      // Try to find any input that might be selected visually (has aria-checked, is focused, or has temp-selected attribute)
      selectedColorOption = /** @type {HTMLInputElement | null} */ (selectedColorFieldset?.querySelector('input[type="radio"][aria-checked="true"], input[type="radio"]:focus, input[type="radio"][data-temp-selected="true"]'));
    }
    
    if (!selectedColorOption) {
      console.log('[QuickAdd] No color selected');
      thumbnailImg.style.display = 'none';
      return;
    }
    
    // If we have a temp-selected input, use its variant ID directly
    if (selectedColorOption.hasAttribute('data-temp-selected')) {
      const variantId = selectedColorOption.dataset.variantId;
      if (variantId) {
        console.log('[QuickAdd] Using temp-selected variant ID:', variantId);
        // Use the direct variant method
        this.#updateThumbnailImageForVariant(modalContent, thumbnailContainer, variantId);
        return;
      }
    }

    // Get the color value (from label or option value)
    const colorLabel = selectedColorOption.closest('label')?.textContent?.trim() || 
                       selectedColorOption.value || 
                       selectedColorOption.dataset.optionValue || '';
    const colorValue = colorLabel.toLowerCase().trim();
    
    console.log('[QuickAdd] Selected color:', colorValue);

    // Get product ID
    const productForm = modalContent.querySelector('product-form-component');
    const productId = productForm instanceof HTMLElement ? productForm.dataset.productId : undefined;
    
    if (!productId) {
      console.log('[QuickAdd] No product ID found');
      thumbnailImg.style.display = 'none';
      return;
    }

    // PRIORITY 1: Try to get image from selected variant
    const variantIdInput = modalContent.querySelector('input[name="id"][ref="variantId"], input[name="id"]');
    console.log('[QuickAdd] Variant ID input found:', !!variantIdInput);
    
    if (variantIdInput && variantIdInput instanceof HTMLInputElement) {
      const variantId = variantIdInput.value;
      console.log('[QuickAdd] Selected variant ID:', variantId);
      
      if (!variantId || variantId === '') {
        console.log('[QuickAdd] Variant ID is empty, trying to find from checked inputs...');
        // Try to find variant ID from checked radio inputs (including disabled/unavailable ones)
        let checkedInput = variantPicker.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
        // If no checked input, try to find the one that was clicked (even if disabled)
        if (!checkedInput) {
          checkedInput = variantPicker.querySelector('input[type="radio"][aria-checked="true"], input[type="radio"]:focus');
        }
        // If still not found, use the selectedColorOption we found earlier
        if (!checkedInput) {
          checkedInput = selectedColorOption;
        }
        if (checkedInput && checkedInput instanceof HTMLInputElement) {
          const variantIdFromInput = checkedInput.dataset.variantId;
          console.log('[QuickAdd] Variant ID from checked input:', variantIdFromInput);
          if (variantIdFromInput) {
            // Use the variant ID from the checked input
            // Try multiple selectors for product JSON
            let productDataScript = modalContent.querySelector('script[type="application/json"][data-product-json]');
            if (!productDataScript) {
              productDataScript = modalContent.querySelector('script[type="application/json"]');
            }
            if (!productDataScript) {
              // Try to find in the document (might be outside modalContent)
              productDataScript = document.querySelector(`script[type="application/json"][data-product-id="${productId}"]`);
            }
            console.log('[QuickAdd] Product data script found:', !!productDataScript);
            if (productDataScript) {
              try {
                const productData = JSON.parse(productDataScript.textContent || '{}');
                console.log('[QuickAdd] Product data parsed, variants count:', productData.variants?.length || 0);
                if (productData.variants && Array.isArray(productData.variants)) {
                  const selectedVariant = productData.variants.find((/** @type {any} */ v) => v.id.toString() === variantIdFromInput.toString());
                  console.log('[QuickAdd] Selected variant found:', !!selectedVariant);
                  if (selectedVariant && selectedVariant.featured_media && selectedVariant.featured_media.preview_image) {
                    let variantImgSrc = selectedVariant.featured_media.preview_image.src;
                    console.log('[QuickAdd] Variant image src:', variantImgSrc);
                    if (variantImgSrc) {
                      // Make absolute URL if relative
                      if (!variantImgSrc.startsWith('http')) {
                        variantImgSrc = new URL(variantImgSrc, window.location.origin).toString();
                      }
                      
                      // Add width parameter
                      try {
                        const url = new URL(variantImgSrc);
                        url.searchParams.set('width', '600');
                        const finalSrc = url.toString();
                        
                        thumbnailImg.src = finalSrc;
                        thumbnailImg.style.display = 'block';
                        thumbnailImg.style.visibility = 'visible';
                        thumbnailImg.style.opacity = '1';
                        thumbnailImg.style.width = '100px';
                        thumbnailImg.style.height = '100px';
                        thumbnailImg.style.objectFit = 'cover';
                        
                        console.log('[QuickAdd] Using variant featured image (PRIORITY 1 - from checked input):', finalSrc);
                        return;
                      } catch (e) {
                        console.error('[QuickAdd] Error creating variant image URL:', e);
                      }
                    }
                  }
                }
              } catch (e) {
                console.error('[QuickAdd] Error parsing product data:', e);
              }
            }
          }
        }
      } else {
        // Try to get variant image from product data JSON
        // Try multiple selectors for product JSON
        let productDataScript = modalContent.querySelector('script[type="application/json"][data-product-json]');
        if (!productDataScript) {
          productDataScript = modalContent.querySelector('script[type="application/json"]');
        }
        if (!productDataScript) {
          // Try to find in the document (might be outside modalContent)
          productDataScript = document.querySelector(`script[type="application/json"][data-product-id="${productId}"]`);
        }
        console.log('[QuickAdd] Product data script found:', !!productDataScript);
        if (productDataScript) {
          try {
            const productData = JSON.parse(productDataScript.textContent || '{}');
            console.log('[QuickAdd] Product data parsed, variants count:', productData.variants?.length || 0);
            if (productData.variants && Array.isArray(productData.variants)) {
              const selectedVariant = productData.variants.find((/** @type {any} */ v) => v.id.toString() === variantId.toString());
              console.log('[QuickAdd] Selected variant found:', !!selectedVariant);
              if (selectedVariant && selectedVariant.featured_media && selectedVariant.featured_media.preview_image) {
                let variantImgSrc = selectedVariant.featured_media.preview_image.src;
                console.log('[QuickAdd] Variant image src:', variantImgSrc);
                if (variantImgSrc) {
                  // Make absolute URL if relative
                  if (!variantImgSrc.startsWith('http')) {
                    variantImgSrc = new URL(variantImgSrc, window.location.origin).toString();
                  }
                  
                  // Add width parameter
                  try {
                    const url = new URL(variantImgSrc);
                    url.searchParams.set('width', '600');
                    const finalSrc = url.toString();
                    
                    thumbnailImg.src = finalSrc;
                    thumbnailImg.style.display = 'block';
                    thumbnailImg.style.visibility = 'visible';
                    thumbnailImg.style.opacity = '1';
                    thumbnailImg.style.width = '100px';
                    thumbnailImg.style.height = '100px';
                    thumbnailImg.style.objectFit = 'cover';
                    
                    console.log('[QuickAdd] Using variant featured image (PRIORITY 1):', finalSrc);
                    return;
                  } catch (e) {
                    console.error('[QuickAdd] Error creating variant image URL:', e);
                  }
                }
              }
            }
          } catch (e) {
            console.error('[QuickAdd] Error parsing product data:', e);
          }
        }
        
        // Try to get from addToCartButton dataset
        const addToCartButton = productForm?.querySelector('[ref="addToCartButton"]');
        console.log('[QuickAdd] Add to cart button found:', !!addToCartButton);
        if (addToCartButton && addToCartButton instanceof HTMLElement && addToCartButton.dataset.productVariantMedia) {
          let variantImgSrc = addToCartButton.dataset.productVariantMedia;
          console.log('[QuickAdd] Variant image from button dataset:', variantImgSrc);
          if (variantImgSrc) {
            // Make absolute URL if relative
            if (!variantImgSrc.startsWith('http')) {
              variantImgSrc = new URL(variantImgSrc, window.location.origin).toString();
            }
            
            // Add width parameter
            try {
              const url = new URL(variantImgSrc);
              url.searchParams.set('width', '600');
              const finalSrc = url.toString();
              
              thumbnailImg.src = finalSrc;
              thumbnailImg.style.display = 'block';
              thumbnailImg.style.visibility = 'visible';
              thumbnailImg.style.opacity = '1';
              thumbnailImg.style.width = '100px';
              thumbnailImg.style.height = '100px';
              thumbnailImg.style.objectFit = 'cover';
              
              console.log('[QuickAdd] Using variant image from addToCartButton (PRIORITY 1):', finalSrc);
              return;
            } catch (e) {
              console.error('[QuickAdd] Error creating variant image URL from button:', e);
            }
          }
        }
      }
    } else {
      console.log('[QuickAdd] Variant ID input not found, trying alternative methods...');
    }

    // PRIORITY 2: Try to get product media from the fetched HTML by variant ID or color alt text
    // First, try to find image by variant ID in the cached HTML
    const currentUrl = this.productPageUrl || '';
    const productPageUrl = currentUrl ? currentUrl.split('?')[0] : '';
    const cachedHtml = productPageUrl ? (this.#cachedHtml.get(productPageUrl) || this.#cachedHtml.get(this.productPageUrl)) : null;
    
    // Try to find image by variant ID first
    if (cachedHtml && variantIdInput && variantIdInput instanceof HTMLInputElement) {
      const variantId = variantIdInput.value;
      if (variantId) {
        // Look for images with data-variant-id or in slideshow-slide with matching variant
        const variantImages = cachedHtml.querySelectorAll(`img[data-variant-id="${variantId}"], slideshow-slide[data-variant-id="${variantId}"] img`);
        if (variantImages.length > 0 && variantImages[0]) {
          const firstVariantImg = variantImages[0];
          let imgSrc = firstVariantImg.getAttribute('src') || firstVariantImg.getAttribute('data-src') || firstVariantImg.getAttribute('data-original-src') || '';
          if (imgSrc) {
            if (!imgSrc.startsWith('http')) {
              imgSrc = new URL(imgSrc, window.location.origin).toString();
            }
            try {
              const url = new URL(imgSrc);
              url.searchParams.set('width', '600');
              const finalSrc = url.toString();
              
              thumbnailImg.src = finalSrc;
              thumbnailImg.style.display = 'block';
              thumbnailImg.style.visibility = 'visible';
              thumbnailImg.style.opacity = '1';
              thumbnailImg.style.width = '100px';
              thumbnailImg.style.height = '100px';
              thumbnailImg.style.objectFit = 'cover';
              
              console.log('[QuickAdd] Using variant image from cached HTML (PRIORITY 2 - by variant ID):', finalSrc);
              return;
            } catch (e) {
              console.error('[QuickAdd] Error creating variant image URL from cached HTML:', e);
            }
          }
        }
      }
    }
    
    if (cachedHtml) {
      // Look for images in the full HTML document that match the color in alt text
      // Search in media gallery areas first
      const mediaGallery = cachedHtml.querySelector('.product-information__media, media-gallery, .product-media-gallery-thumbs-left');
      const searchArea = mediaGallery || cachedHtml;
      
      // Look for images in slideshow slides first (product media gallery)
      const slideshowSlides = searchArea.querySelectorAll('slideshow-slide img[alt], .product-media img[alt], .product-media-container img[alt]');
      const allImages = slideshowSlides.length > 0 ? Array.from(slideshowSlides) : Array.from(searchArea.querySelectorAll('img[alt]'));
      
      console.log('[QuickAdd] Found images in HTML:', allImages.length);
      
      // Find the FIRST image that matches the color
      for (const img of allImages) {
        const altText = (img.getAttribute('alt') || '').toLowerCase().trim();
        console.log('[QuickAdd] Checking image alt:', altText, 'against color:', colorValue);
        
        // Check if alt text contains the color value (exact match or contains)
        if (altText && colorValue) {
          // Try exact match first, then contains
          const colorWords = colorValue.split(/\s+/);
          const altWords = altText.split(/\s+/);
          
          const hasMatch = colorWords.some(word => 
            altWords.some(altWord => altWord === word || altWord.includes(word) || word.includes(altWord))
          ) || altText.includes(colorValue) || colorValue.includes(altText);
          
          if (hasMatch) {
            // Found matching image - get the first one only
            let imgSrc = img.getAttribute('src') || img.getAttribute('data-src') || img.getAttribute('data-original-src') || '';
            
            // If src is relative, make it absolute
            if (imgSrc && !imgSrc.startsWith('http')) {
              imgSrc = new URL(imgSrc, window.location.origin).toString();
            }
            
            if (imgSrc) {
              // Clean URL and add width parameter
              try {
              const url = new URL(imgSrc);
              url.searchParams.set('width', '600');
              const finalSrc = url.toString();
                
                // Get the thumbnail container and image
                const container = modalContent.querySelector('.quick-add-thumbnail-container');
                const imgElement = container ? container.querySelector('.quick-add-thumbnail') : null;
                
                if (imgElement) {
                  const img = /** @type {HTMLImageElement} */ (imgElement);
                  img.src = finalSrc;
                  img.style.display = 'block';
                  img.style.visibility = 'visible';
                  img.style.opacity = '1';
                  img.style.width = '100px';
                  img.style.height = '100px';
                  img.style.objectFit = 'cover';
                  
                  if (container) {
                    const containerEl = /** @type {HTMLElement} */ (container);
                    containerEl.style.display = 'flex';
                    containerEl.style.visibility = 'visible';
                    containerEl.style.opacity = '1';
                  }
                  
                  console.log('[QuickAdd] Found matching image (first match):', finalSrc);
                  console.log('[QuickAdd] Image element:', img);
                  console.log('[QuickAdd] Container element:', container);
                  
                  // Force a reflow to ensure image is displayed
                  void img.offsetHeight;
                  
                  // Add load event to verify image loads
                  img.onload = () => {
                    console.log('[QuickAdd] Image loaded successfully');
                    img.style.display = 'block';
                    img.style.visibility = 'visible';
                    img.style.opacity = '1';
                  };
                  
                  img.onerror = () => {
                    console.error('[QuickAdd] Image failed to load:', finalSrc);
                  };
                  
                  return;
                } else {
                  console.error('[QuickAdd] Thumbnail image element not found');
                }
              } catch (e) {
                console.error('[QuickAdd] Error creating image URL:', e);
              }
            }
          }
        }
      }
    }

    // Fallback: try to get from product form component
    if (productForm) {
      const addToCartButton = /** @type {HTMLElement | null} */ (productForm.querySelector('[ref="addToCartButton"]'));
      if (addToCartButton && addToCartButton.dataset.productVariantMedia) {
        console.log('[QuickAdd] Using fallback image from addToCartButton');
        thumbnailImg.src = addToCartButton.dataset.productVariantMedia;
        thumbnailImg.style.display = 'block';
        return;
      }
    }

    // If no matching image found, hide thumbnail
    console.log('[QuickAdd] No matching image found for color:', colorValue);
    thumbnailImg.style.display = 'none';
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    console.log('[QuickAdd] Dialog connected');
    console.log('[QuickAdd] Dialog refs:', this.refs);
    console.log('[QuickAdd] Dialog element:', this.refs.dialog);
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }
  
  showDialog() {
    console.log('[QuickAdd] QuickAddDialog.showDialog called');
    console.log('[QuickAdd] Dialog refs:', this.refs);
    console.log('[QuickAdd] Dialog element:', this.refs.dialog);
    if (!this.refs.dialog) {
      console.error('[QuickAdd] Dialog ref not found!');
      return;
    }
    console.log('[QuickAdd] Dialog open before showDialog:', this.refs.dialog.open);
    console.log('[QuickAdd] Dialog hasAttribute open:', this.refs.dialog.hasAttribute('open'));
    
    // Force dialog size BEFORE opening
    const dialog = this.refs.dialog;
    const isMobile = window.innerWidth <= 750;
    
    if (isMobile) {
      // Mobile: full screen, no scroll
      dialog.style.setProperty('max-height', '100vh', 'important');
      dialog.style.setProperty('height', '100vh', 'important');
      dialog.style.setProperty('width', '100vw', 'important');
      dialog.style.setProperty('max-width', '100vw', 'important');
      dialog.style.setProperty('margin', '0', 'important');
      dialog.style.setProperty('padding', '0', 'important');
      dialog.style.setProperty('border', 'none', 'important');
      dialog.style.setProperty('border-radius', '0', 'important');
      dialog.style.setProperty('top', '0', 'important');
      dialog.style.setProperty('left', '0', 'important');
      dialog.style.setProperty('right', '0', 'important');
      dialog.style.setProperty('bottom', '0', 'important');
      console.log('[QuickAdd] Forced dialog size for mobile: full screen');
    } else {
      // Desktop: centered modal with scroll
      const wideContentWidth = getComputedStyle(document.documentElement).getPropertyValue('--wide-content-width').trim() || '1400px';
      dialog.style.setProperty('max-width', wideContentWidth, 'important');
      dialog.style.setProperty('width', 'auto', 'important');
      dialog.style.setProperty('height', 'auto', 'important');
      dialog.style.setProperty('max-height', '90vh', 'important');
      dialog.style.setProperty('overflow', 'visible', 'important');
      dialog.style.setProperty('margin', 'auto', 'important');
      console.log('[QuickAdd] Forced dialog size for desktop before opening, max-width:', wideContentWidth);
    }
    
    dialog.style.setProperty('padding', '0', 'important');
    dialog.style.setProperty('z-index', '9999', 'important');
    
    super.showDialog();
    console.log('[QuickAdd] super.showDialog() called');
    // Wait a bit and check if dialog is actually open
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const styles = window.getComputedStyle(dialog);
        console.log('[QuickAdd] Dialog open after showDialog:', dialog.open);
        console.log('[QuickAdd] Dialog computed style display:', styles.display);
        console.log('[QuickAdd] Dialog computed style visibility:', styles.visibility);
        console.log('[QuickAdd] Dialog computed style opacity:', styles.opacity);
        console.log('[QuickAdd] Dialog computed style z-index:', styles.zIndex);
        console.log('[QuickAdd] Dialog computed style position:', styles.position);
        console.log('[QuickAdd] Dialog computed style max-width:', styles.maxWidth);
        console.log('[QuickAdd] Dialog computed style width:', styles.width);
        
        // Check content size
        const modalContent = dialog.querySelector('#quick-add-modal-content');
        if (modalContent) {
          const contentRect = modalContent.getBoundingClientRect();
          const contentStyles = window.getComputedStyle(modalContent);
          console.log('[QuickAdd] Modal content size:', { width: contentRect.width, height: contentRect.height });
          console.log('[QuickAdd] Modal content computed width:', contentStyles.width);
          console.log('[QuickAdd] Modal content computed max-width:', contentStyles.maxWidth);
          
          // If content has size but dialog doesn't, force dialog to match content
          if (window.innerWidth >= 750 && contentRect.width > 0 && dialog.offsetWidth === 0) {
            dialog.style.setProperty('width', `${contentRect.width}px`, 'important');
            dialog.style.setProperty('min-width', `${contentRect.width}px`, 'important');
            console.log('[QuickAdd] Forced dialog width to match content:', contentRect.width);
          }
          
          // Ensure scroll works on modal content (desktop only)
          if (modalContent) {
            const contentEl = /** @type {HTMLElement} */ (modalContent);
            const isMobile = window.innerWidth <= 750;
            
            if (isMobile) {
              // Mobile: no scroll, adapt to content
              contentEl.style.setProperty('display', 'flex', 'important');
              contentEl.style.setProperty('flex-direction', 'column', 'important');
              contentEl.style.setProperty('overflow-y', 'visible', 'important');
              contentEl.style.setProperty('overflow-x', 'hidden', 'important');
              contentEl.style.setProperty('max-height', 'none', 'important');
              contentEl.style.setProperty('height', 'auto', 'important');
              contentEl.style.setProperty('flex', '1 1 auto', 'important');
            } else {
              // Desktop: with scroll
              const dialogRect = dialog.getBoundingClientRect();
              const availableHeight = Math.min(window.innerHeight * 0.85, dialogRect.height || window.innerHeight * 0.85);
              
              contentEl.style.setProperty('display', 'block', 'important');
              contentEl.style.setProperty('overflow-y', 'auto', 'important');
              contentEl.style.setProperty('overflow-x', 'hidden', 'important');
              contentEl.style.setProperty('max-height', `${availableHeight}px`, 'important');
              contentEl.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
              contentEl.style.setProperty('position', 'relative', 'important');
            }
            
            // Prevent scroll events from bubbling to body/dialog
            const handleWheel = (/** @type {WheelEvent} */ e) => {
              const target = /** @type {HTMLElement} */ (e.target);
              const isScrollable = contentEl.scrollHeight > contentEl.clientHeight;
              if (isScrollable && (target === contentEl || contentEl.contains(target))) {
                e.stopPropagation();
              }
            };
            
            const handleTouchMove = (/** @type {TouchEvent} */ e) => {
              const target = /** @type {HTMLElement} */ (e.target);
              const isScrollable = contentEl.scrollHeight > contentEl.clientHeight;
              if (isScrollable && (target === contentEl || contentEl.contains(target))) {
                e.stopPropagation();
              }
            };
            
            contentEl.addEventListener('wheel', handleWheel, { passive: true, capture: true });
            contentEl.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true });
            
            console.log('[QuickAdd] Scroll enabled on modal content', {
              scrollHeight: contentEl.scrollHeight,
              clientHeight: contentEl.clientHeight,
              maxHeight: contentEl.style.maxHeight
            });
          }
          
          // Force thumbnail update when dialog opens
          setTimeout(() => {
            const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
            if (thumbnailContainer) {
              console.log('[QuickAdd] Forcing thumbnail update after dialog opens...');
              // Trigger variant update event to update thumbnail
              const variantPicker = modalContent.querySelector('variant-picker');
              if (variantPicker) {
                // Dispatch a change event to trigger thumbnail update
                const checkedInput = variantPicker.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
                if (checkedInput) {
                  checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            }
          }, 500);
        }
        
        console.log('[QuickAdd] Dialog getBoundingClientRect:', dialog.getBoundingClientRect());
        console.log('[QuickAdd] Dialog offsetWidth:', dialog.offsetWidth, 'offsetHeight:', dialog.offsetHeight);
        
        // Check backdrop
        const backdrop = dialog.querySelector('::backdrop') || document.querySelector('dialog::backdrop');
        console.log('[QuickAdd] Backdrop element:', backdrop);
        if (backdrop) {
          const backdropStyles = window.getComputedStyle(backdrop);
          console.log('[QuickAdd] Backdrop display:', backdropStyles.display);
          console.log('[QuickAdd] Backdrop opacity:', backdropStyles.opacity);
        }
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

    // Update thumbnail when variant changes
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      const thumbnailContainer = modalContent.querySelector('.quick-add-thumbnail-container');
      if (thumbnailContainer) {
        // Get variant image from event (PRIORITY - direct from event)
        const variantResource = event.detail.resource;
        if (variantResource?.featured_media?.preview_image?.src) {
          const thumbnailImg = /** @type {HTMLImageElement | null} */ (thumbnailContainer.querySelector('.quick-add-thumbnail'));
          if (thumbnailImg) {
            let variantImgSrc = variantResource.featured_media.preview_image.src;
            // Make absolute URL if relative
            if (!variantImgSrc.startsWith('http')) {
              variantImgSrc = new URL(variantImgSrc, window.location.origin).toString();
            }
            
            // Add width parameter
            try {
              const url = new URL(variantImgSrc);
              url.searchParams.set('width', '600');
              const finalSrc = url.toString();
              
              thumbnailImg.src = finalSrc;
              thumbnailImg.style.display = 'block';
              thumbnailImg.style.visibility = 'visible';
              thumbnailImg.style.opacity = '1';
              thumbnailImg.style.width = '100px';
              thumbnailImg.style.height = '100px';
              thumbnailImg.style.objectFit = 'cover';
              
              console.log('[QuickAdd] Thumbnail updated from variantUpdate event:', finalSrc);
              return;
            } catch (e) {
              console.error('[QuickAdd] Error creating variant image URL from event:', e);
            }
          }
        }
        
        // Fallback: use the updateThumbnailImage method
        const quickAddComponent = document.querySelector('quick-add-component');
        if (quickAddComponent && quickAddComponent instanceof QuickAddComponent) {
          // @ts-ignore - accessing private method
          quickAddComponent['#updateThumbnailImage'](modalContent, thumbnailContainer);
        }
      }
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
